import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  Alert,
  AutoComplete,
  Avatar,
  Button,
  Card,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadChangeParam } from 'antd/lib/upload';
import {
  ReloadOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { useDebouncedCallback } from 'use-debounce';

import {
  createPresignedAiClipperChannelLogoUrl,
  fetchAiClipperSettings,
  fetchAiClipperUserAccessList,
  setAiClipperSettings,
  setAiClipperUserAccess,
} from 'api/aiClipperPost/client';
import { AiClipperSetting, AiClipperUserAccess } from 'api/aiClipperPost/types';
import { GET_STORES_BY_NAME_AND_EMAIL } from 'api/store/queries';
import {
  GetStoresByNameAndEmail,
  GetStoresByNameAndEmailVariables,
} from 'api/store/types/GetStoresByNameAndEmail';
const { Title, Text } = Typography;

const CHANNEL_LOGO_CONTENT_TYPES = ['image/png', 'image/jpeg'] as const;

function resolveChannelLogoContentType(
  file: File
): typeof CHANNEL_LOGO_CONTENT_TYPES[number] {
  const normalized = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
  if (
    CHANNEL_LOGO_CONTENT_TYPES.includes(
      normalized as typeof CHANNEL_LOGO_CONTENT_TYPES[number]
    )
  ) {
    return normalized as typeof CHANNEL_LOGO_CONTENT_TYPES[number];
  }

  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';

  throw new Error('Only PNG and JPEG images are supported.');
}

const DEFAULT_USER_CONTENT_CONFIG = {
  minDurationMins: 10,
  numClips: 5,
  burnSubtitles: true,
  mode: 'qwen',
};

const DEFAULT_AUTO_APPROVAL_HOURS = 48;
const EXPIRING_SOON_DAYS = 14;

const AIClipperSettings: React.FC = () => {
  const apolloClient = useApolloClient();

  // --- Interview auto-approval (48h cron) ---
  const [autoApprovalEnabled, setAutoApprovalEnabled] = useState(false);
  const [autoApprovalHours, setAutoApprovalHours] = useState<number>(
    DEFAULT_AUTO_APPROVAL_HOURS
  );

  // --- Global post auto-generation setting ---
  const [enabled, setEnabled] = useState(false);
  const [minDurationMins, setMinDurationMins] = useState<number>(
    DEFAULT_USER_CONTENT_CONFIG.minDurationMins
  );
  const [numClips, setNumClips] = useState<number>(
    DEFAULT_USER_CONTENT_CONFIG.numClips
  );
  const [burnSubtitles, setBurnSubtitles] = useState<boolean>(
    DEFAULT_USER_CONTENT_CONFIG.burnSubtitles
  );
  const [mode, setMode] = useState<string>(DEFAULT_USER_CONTENT_CONFIG.mode);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  // --- Manual paid access ---
  const [accessRows, setAccessRows] = useState<AiClipperUserAccess[]>([]);
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessQuery, setAccessQuery] = useState('');

  // --- Grant access form ---
  const [grantUserSearch, setGrantUserSearch] = useState('');
  const [grantUserId, setGrantUserId] = useState('');
  const [grantValidUntil, setGrantValidUntil] = useState<Moment | null>(null);
  const [grantNote, setGrantNote] = useState('');
  const [granting, setGranting] = useState(false);
  const [userSearchOptions, setUserSearchOptions] = useState<
    { value: string; label: React.ReactNode; userId: string }[]
  >([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  // --- Extend access modal ---
  const [extendRow, setExtendRow] = useState<AiClipperUserAccess | null>(null);
  const [extendDate, setExtendDate] = useState<Moment | null>(null);
  const [extending, setExtending] = useState(false);

  const [logoRow, setLogoRow] = useState<AiClipperUserAccess | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [savingLogo, setSavingLogo] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const applySetting = useCallback((settings: AiClipperSetting[]) => {
    const autoApproval = settings.find((s) => s.id === 'AutoApproval');
    setAutoApprovalEnabled(!!autoApproval?.isEnabled);
    setAutoApprovalHours(
      autoApproval?.config?.autoApprovalHours ?? DEFAULT_AUTO_APPROVAL_HOURS
    );

    const postAutoGeneration = settings.find(
      (s) => s.id === 'PostAutoGeneration'
    );
    setEnabled(!!postAutoGeneration?.isEnabled);
    const cfg = postAutoGeneration?.config;
    setMinDurationMins(
      cfg?.minDurationMs != null
        ? Math.round(cfg.minDurationMs / 60000)
        : DEFAULT_USER_CONTENT_CONFIG.minDurationMins
    );
    setNumClips(cfg?.numClips ?? DEFAULT_USER_CONTENT_CONFIG.numClips);
    setBurnSubtitles(
      cfg?.burnSubtitles ?? DEFAULT_USER_CONTENT_CONFIG.burnSubtitles
    );
    setMode(cfg?.mode ?? DEFAULT_USER_CONTENT_CONFIG.mode);
  }, []);

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      applySetting(await fetchAiClipperSettings(apolloClient));
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load settings.');
    } finally {
      setSettingsLoading(false);
    }
  }, [apolloClient, applySetting]);

  const loadAccess = useCallback(
    async (query: string) => {
      setAccessLoading(true);
      try {
        const page = await fetchAiClipperUserAccessList(apolloClient, {
          query: query.trim() || undefined,
          limit: 50,
          offset: 0,
        });
        setAccessRows(page.entities);
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load access list.');
      } finally {
        setAccessLoading(false);
      }
    },
    [apolloClient]
  );

  const searchUsersDebounced = useDebouncedCallback(async (q: string) => {
    if (!q.trim()) {
      setUserSearchOptions([]);
      return;
    }
    setUserSearchLoading(true);
    try {
      const result = await apolloClient.query<
        GetStoresByNameAndEmail,
        GetStoresByNameAndEmailVariables
      >({
        query: GET_STORES_BY_NAME_AND_EMAIL,
        variables: { searchTerm: q.trim() },
        fetchPolicy: 'network-only',
      });
      const seen = new Set<string>();
      const entities = [
        ...(result.data?.nameStores?.entities ?? []),
        ...(result.data?.emailStores?.entities ?? []),
      ].filter((u) => (seen.has(u.id) ? false : seen.add(u.id)));

      setUserSearchOptions(
        entities.map((u) => {
          const name = [u.firstName, u.lastName].filter(Boolean).join(' ');
          const slug = u.slug ? `@${u.slug}` : '';
          const avatarURL = u.storeDetails?.avatarURL;
          const label = (
            <Space align="center" size={8}>
              <Avatar
                src={avatarURL ?? undefined}
                icon={!avatarURL && <UserOutlined />}
                size={32}
              />
              <Space direction="vertical" size={0} style={{ lineHeight: 1.3 }}>
                <span style={{ fontWeight: 600 }}>{name || slug || u.id}</span>
                {slug && (
                  <span style={{ fontSize: 12, color: '#888' }}>{slug}</span>
                )}
              </Space>
            </Space>
          );
          return {
            value: slug || u.id,
            label,
            userId: u.id,
          };
        })
      );
    } catch {
      setUserSearchOptions([]);
    } finally {
      setUserSearchLoading(false);
    }
  }, 300);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    loadAccess(accessQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAccess]);

  const handleSaveAutoApproval = useCallback(async () => {
    setSavingSettings(true);
    setError(null);
    try {
      const settings = await setAiClipperSettings(apolloClient, {
        AutoApproval: {
          isEnabled: autoApprovalEnabled,
          config: { autoApprovalHours },
        },
      });
      applySetting(settings);
      message.success('Auto-approval settings saved.');
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save auto-approval settings.');
    } finally {
      setSavingSettings(false);
    }
  }, [apolloClient, autoApprovalEnabled, autoApprovalHours, applySetting]);

  const handleSaveSettings = useCallback(async () => {
    setSavingSettings(true);
    setError(null);
    try {
      const settings = await setAiClipperSettings(apolloClient, {
        PostAutoGeneration: {
          isEnabled: enabled,
          config: {
            minDurationMs: minDurationMins * 60000,
            numClips,
            burnSubtitles,
            mode,
          },
        },
      });
      applySetting(settings);
      message.success('Settings saved.');
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save settings.');
    } finally {
      setSavingSettings(false);
    }
  }, [
    apolloClient,
    enabled,
    minDurationMins,
    numClips,
    burnSubtitles,
    mode,
    applySetting,
  ]);

  const handleGrantAccess = useCallback(async () => {
    if (!grantUserId.trim()) {
      message.warning('Select or enter a user first.');
      return;
    }
    setGranting(true);
    setError(null);
    try {
      await setAiClipperUserAccess(apolloClient, {
        userId: grantUserId.trim(),
        isEnabled: true,
        validUntil: grantValidUntil
          ? grantValidUntil.endOf('day').toISOString()
          : null,
        note: grantNote.trim() || null,
      });
      message.success('Access granted.');
      setGrantUserSearch('');
      setGrantUserId('');
      setGrantValidUntil(null);
      setGrantNote('');
      setUserSearchOptions([]);
      loadAccess(accessQuery);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to grant access.');
    } finally {
      setGranting(false);
    }
  }, [
    apolloClient,
    grantUserId,
    grantValidUntil,
    grantNote,
    accessQuery,
    loadAccess,
  ]);

  const handleToggleAccess = useCallback(
    async (row: AiClipperUserAccess, nextEnabled: boolean) => {
      setError(null);
      try {
        await setAiClipperUserAccess(apolloClient, {
          userId: row.userId,
          isEnabled: nextEnabled,
          validUntil: row.validUntil,
          note: row.note,
        });
        message.success(nextEnabled ? 'Access enabled.' : 'Access disabled.');
        loadAccess(accessQuery);
      } catch (e: any) {
        setError(e?.message ?? 'Failed to update access.');
      }
    },
    [apolloClient, accessQuery, loadAccess]
  );

  const handleExtendAccess = useCallback(async () => {
    if (!extendRow || !extendDate) return;
    setExtending(true);
    setError(null);
    try {
      await setAiClipperUserAccess(apolloClient, {
        userId: extendRow.userId,
        isEnabled: extendRow.isEnabled,
        validUntil: extendDate.endOf('day').toISOString(),
        note: extendRow.note,
      });
      message.success('Access extended.');
      setExtendRow(null);
      setExtendDate(null);
      loadAccess(accessQuery);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to extend access.');
    } finally {
      setExtending(false);
    }
  }, [apolloClient, extendRow, extendDate, accessQuery, loadAccess]);

  const clearLogoModal = useCallback(() => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoRow(null);
    setLogoFile(null);
    setLogoPreview('');
  }, [logoPreview]);

  const handleSaveLogo = useCallback(async () => {
    if (!logoRow || !logoFile) return;
    setSavingLogo(true);
    setError(null);
    try {
      const contentType = resolveChannelLogoContentType(logoFile);
      const presigned = await createPresignedAiClipperChannelLogoUrl(
        apolloClient,
        { contentType }
      );
      const fields: Record<string, string> = JSON.parse(presigned.fields);
      const formData = new FormData();
      formData.append('key', presigned.key);
      formData.append('Content-Type', contentType);
      Object.entries(fields).forEach(([k, v]) => {
        if (k !== 'key' && k !== 'Content-Type') formData.append(k, v);
      });
      formData.append('file', logoFile); // must be last for S3 presigned POST
      const uploadRes = await fetch(presigned.url, {
        method: 'POST',
        body: formData,
      });
      if (!uploadRes.ok) {
        throw new Error(`S3 upload failed: ${uploadRes.status}`);
      }
      await setAiClipperUserAccess(apolloClient, {
        userId: logoRow.userId,
        isEnabled: logoRow.isEnabled,
        validUntil: logoRow.validUntil,
        note: logoRow.note,
        channelLogoKey: presigned.key,
      });
      message.success('Channel logo saved.');
      clearLogoModal();
      loadAccess(accessQuery);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save logo.');
    } finally {
      setSavingLogo(false);
    }
  }, [
    apolloClient,
    logoRow,
    logoFile,
    accessQuery,
    loadAccess,
    clearLogoModal,
  ]);

  const handleRemoveLogo = useCallback(async () => {
    if (!logoRow) return;
    setSavingLogo(true);
    setError(null);
    try {
      await setAiClipperUserAccess(apolloClient, {
        userId: logoRow.userId,
        isEnabled: logoRow.isEnabled,
        validUntil: logoRow.validUntil,
        note: logoRow.note,
        channelLogoKey: null,
      });
      message.success('Channel logo removed.');
      clearLogoModal();
      loadAccess(accessQuery);
    } catch (e: any) {
      setError(e?.message ?? 'Failed to remove logo.');
    } finally {
      setSavingLogo(false);
    }
  }, [apolloClient, logoRow, accessQuery, loadAccess, clearLogoModal]);

  const accessColumns = useMemo(
    () => [
      {
        title: 'User',
        key: 'user',
        render: (_: unknown, row: AiClipperUserAccess) => {
          const name = [row.user?.firstName, row.user?.lastName]
            .filter(Boolean)
            .join(' ');
          return (
            <Space align="center" size={8}>
              <Avatar
                src={row.user?.avatarUrl ?? undefined}
                icon={!row.user?.avatarUrl && <UserOutlined />}
                size={32}
              />
              <Space direction="vertical" size={0}>
                <Text strong>{name || row.user?.slug || row.userId}</Text>
                {row.user?.slug && (
                  <Text type="secondary">@{row.user.slug}</Text>
                )}
              </Space>
            </Space>
          );
        },
      },
      {
        title: 'Status',
        key: 'status',
        render: (_: unknown, row: AiClipperUserAccess) =>
          row.isActive ? (
            <Tag color="green">Active</Tag>
          ) : row.isEnabled ? (
            <Tag color="orange">Expired</Tag>
          ) : (
            <Tag>Disabled</Tag>
          ),
      },
      {
        title: 'Valid until',
        dataIndex: 'validUntil',
        key: 'validUntil',
        render: (v: string | null) =>
          v ? moment(v).format('YYYY-MM-DD') : '—',
      },
      {
        title: 'Note',
        dataIndex: 'note',
        key: 'note',
        render: (v: string | null) => v || '—',
      },
      {
        title: 'Logo',
        key: 'logo',
        render: (_: unknown, row: AiClipperUserAccess) =>
          row.channelLogoUrl ? (
            <img
              src={row.channelLogoUrl}
              alt="logo"
              style={{
                width: 32,
                height: 32,
                objectFit: 'cover',
                borderRadius: 4,
              }}
            />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              —
            </Text>
          ),
      },
      {
        title: 'Action',
        key: 'action',
        render: (_: unknown, row: AiClipperUserAccess) => {
          const daysLeft = row.validUntil
            ? moment(row.validUntil).diff(moment(), 'days')
            : null;
          const isExpiringSoon =
            row.isActive && daysLeft !== null && daysLeft <= EXPIRING_SOON_DAYS;

          return (
            <Space>
              <Switch
                checked={row.isEnabled}
                onChange={(checked) => handleToggleAccess(row, checked)}
                checkedChildren="On"
                unCheckedChildren="Off"
              />
              {row.validUntil && (
                <Button
                  size="small"
                  type={isExpiringSoon ? 'default' : 'text'}
                  danger={isExpiringSoon}
                  onClick={() => {
                    setExtendRow(row);
                    setExtendDate(moment(row.validUntil));
                  }}
                >
                  {isExpiringSoon ? `Extend (${daysLeft}d)` : 'Edit expiry'}
                </Button>
              )}
              <Button
                size="small"
                type="text"
                onClick={() => {
                  setLogoRow(row);
                }}
              >
                Edit logo
              </Button>
            </Space>
          );
        },
      },
    ],
    [handleToggleAccess]
  );

  return (
    <div style={{ padding: 24 }}>
      <Title level={4}>AI Clipper Settings</Title>
      <Text type="secondary">
        Interview auto-approval, post auto-generation, and manual paid access.
      </Text>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ margin: '16px 0' }}
        />
      )}

      <Card
        title="Interview auto-approval"
        loading={settingsLoading}
        style={{ marginTop: 16 }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space>
            <Text strong>Enabled</Text>
            <Switch
              checked={autoApprovalEnabled}
              onChange={setAutoApprovalEnabled}
            />
            <Text type="secondary">
              When on, pending interview clips are auto-accepted after the
              configured window.
            </Text>
          </Space>

          <Space direction="vertical" size={2}>
            <Text>Auto-approval window (hours)</Text>
            <InputNumber
              min={1}
              value={autoApprovalHours}
              onChange={(v) => setAutoApprovalHours(Number(v) || 1)}
              style={{ width: 180 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Default 48h — applies to interview clip candidates only.
            </Text>
          </Space>

          <div>
            <Button
              type="primary"
              loading={savingSettings}
              onClick={handleSaveAutoApproval}
            >
              Save auto-approval
            </Button>
          </div>
        </Space>
      </Card>

      <Card
        title="Global post auto generation"
        loading={settingsLoading}
        style={{ marginTop: 24 }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space>
            <Text strong>Enabled</Text>
            <Switch checked={enabled} onChange={setEnabled} />
            <Text type="secondary">
              When off, no organic uploads are sent to the clipper.
            </Text>
          </Space>

          <Space wrap size="large">
            <Space direction="vertical" size={2}>
              <Text>Min duration (min)</Text>
              <InputNumber
                min={1}
                step={1}
                value={minDurationMins}
                onChange={(v) =>
                  setMinDurationMins(
                    v != null
                      ? Number(v)
                      : DEFAULT_USER_CONTENT_CONFIG.minDurationMins
                  )
                }
                style={{ width: 180 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Only videos strictly longer than this are clipped (default 10
                min).
              </Text>
            </Space>

            <Space direction="vertical" size={2}>
              <Text>Number of clips</Text>
              <InputNumber
                min={1}
                value={numClips}
                onChange={(v) => setNumClips(Number(v) || 1)}
                style={{ width: 140 }}
              />
            </Space>

            <Space direction="vertical" size={2}>
              <Text>Burn subtitles</Text>
              <Switch checked={burnSubtitles} onChange={setBurnSubtitles} />
            </Space>

            <Space direction="vertical" size={2}>
              <Text>Mode</Text>
              <Input
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                style={{ width: 140 }}
              />
            </Space>
          </Space>

          <div>
            <Button
              type="primary"
              loading={savingSettings}
              onClick={handleSaveSettings}
            >
              Save settings
            </Button>
          </div>
        </Space>
      </Card>

      <Card title="Manual paid access" style={{ marginTop: 24 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap align="end" size="large">
            <Space direction="vertical" size={2}>
              <Text>User (name, slug, or UUID)</Text>
              <AutoComplete
                value={grantUserSearch}
                options={userSearchOptions}
                style={{ width: 320 }}
                placeholder="Search by name or @slug…"
                notFoundContent={
                  userSearchLoading
                    ? 'Searching…'
                    : grantUserSearch.trim()
                    ? 'No users found'
                    : null
                }
                onChange={(val: string) => {
                  setGrantUserSearch(val);
                  setGrantUserId('');
                  searchUsersDebounced(val);
                }}
                onSelect={(val: string, opt: any) => {
                  setGrantUserSearch(val);
                  setGrantUserId(opt.userId);
                }}
              />
            </Space>
            <Space direction="vertical" size={4}>
              <Text>Valid until (optional)</Text>
              <Space wrap>
                {[1, 2, 3, 6, 12].map((months) => {
                  const target = moment().add(months, 'months');
                  return (
                    <Button
                      key={months}
                      size="small"
                      type={
                        grantValidUntil?.isSame(target, 'day')
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => setGrantValidUntil(target)}
                    >
                      +{months}mo
                    </Button>
                  );
                })}
              </Space>
              <DatePicker
                value={grantValidUntil as any}
                onChange={(d) => setGrantValidUntil(d as Moment | null)}
                allowClear
              />
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Note (optional)</Text>
              <Input
                placeholder="Reason / reference"
                value={grantNote}
                onChange={(e) => setGrantNote(e.target.value)}
                style={{ width: 240 }}
              />
            </Space>
            <Button
              type="primary"
              loading={granting}
              onClick={handleGrantAccess}
            >
              Grant / update access
            </Button>
          </Space>

          <Divider style={{ margin: '8px 0' }} />

          <Space>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by slug or name…"
              value={accessQuery}
              onChange={(e) => setAccessQuery(e.target.value)}
              onPressEnter={() => loadAccess(accessQuery)}
              allowClear
              style={{ width: 280 }}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadAccess(accessQuery)}
              loading={accessLoading}
            >
              Refresh
            </Button>
          </Space>

          <Table<AiClipperUserAccess>
            rowKey="id"
            size="small"
            loading={accessLoading}
            dataSource={accessRows}
            columns={accessColumns}
            pagination={false}
            locale={{ emptyText: 'No access records.' }}
          />
        </Space>
      </Card>

      <Modal
        title="Extend access"
        visible={!!extendRow}
        onCancel={() => {
          setExtendRow(null);
          setExtendDate(null);
        }}
        onOk={handleExtendAccess}
        okText="Extend"
        confirmLoading={extending}
        okButtonProps={{ disabled: !extendDate }}
      >
        {extendRow && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Text>
              Extending access for{' '}
              <Text strong>
                {[extendRow.user?.firstName, extendRow.user?.lastName]
                  .filter(Boolean)
                  .join(' ') ||
                  extendRow.user?.slug ||
                  extendRow.userId}
              </Text>
              {extendRow.validUntil && (
                <>
                  {' '}
                  (currently expires{' '}
                  <Text strong>
                    {moment(extendRow.validUntil).format('YYYY-MM-DD')}
                  </Text>
                  )
                </>
              )}
            </Text>
            <Space direction="vertical" size={8}>
              <Text>Quick extend</Text>
              <Space wrap>
                {[1, 2, 3, 6, 12].map((months) => {
                  const target = moment().add(months, 'months');
                  return (
                    <Button
                      key={months}
                      size="small"
                      type={
                        extendDate?.isSame(target, 'day')
                          ? 'primary'
                          : 'default'
                      }
                      onClick={() => setExtendDate(target)}
                    >
                      +{months}mo
                    </Button>
                  );
                })}
              </Space>
            </Space>
            <Space direction="vertical" size={2}>
              <Text>Or pick a date</Text>
              <DatePicker
                value={extendDate as any}
                onChange={(d) => setExtendDate(d as Moment | null)}
                disabledDate={(d) => d && d.isBefore(moment(), 'day')}
                allowClear={false}
              />
            </Space>
          </Space>
        )}
      </Modal>
      <Modal
        title="Channel logo"
        visible={!!logoRow}
        onCancel={clearLogoModal}
        onOk={handleSaveLogo}
        okText="Save"
        confirmLoading={savingLogo}
        okButtonProps={{ disabled: !logoFile }}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {!logoFile && logoRow?.channelLogoUrl && (
            <Space direction="vertical" size={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Current logo
              </Text>
              <img
                src={logoRow.channelLogoUrl}
                alt="current logo"
                style={{
                  maxWidth: '100%',
                  maxHeight: 120,
                  objectFit: 'contain',
                  borderRadius: 4,
                }}
              />
            </Space>
          )}
          {logoFile && logoPreview && (
            <Space direction="vertical" size={4}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                New logo preview
              </Text>
              <img
                src={logoPreview}
                alt="new logo preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 120,
                  objectFit: 'contain',
                  borderRadius: 4,
                }}
              />
            </Space>
          )}
          <Upload
            accept="image/png,image/jpeg"
            maxCount={1}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info: UploadChangeParam) => {
              const file =
                info.file.originFileObj ?? (info.file as unknown as File);
              if (file) {
                if (logoPreview) {
                  URL.revokeObjectURL(logoPreview);
                }
                setLogoFile(file);
                setLogoPreview(URL.createObjectURL(file));
              }
            }}
          >
            <Button>
              {logoFile ? 'Change file' : 'Select image (PNG or JPEG)'}
            </Button>
          </Upload>
          {logoFile && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {logoFile.name}
            </Text>
          )}
          {logoRow?.channelLogoKey && (
            <Button
              size="small"
              danger
              loading={savingLogo}
              onClick={handleRemoveLogo}
            >
              Remove logo
            </Button>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default AIClipperSettings;
